import { createContext, useContext, useEffect, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { getDaybook } from '../redux/actions/daybook';
import { useUser } from './UserContext';
import { useGlobal } from './GlobalContext';

const DaybookContext = createContext({
   daybooks: [],
   paymentDaybooks: [],
   daybook: null,
   salesDaybooks: [],
   salesChallanDaybooks: [],
   salesOrderDaybooks: [],
   salesReturnDaybooks: [],
   purchaseDaybooks: [],
   purchaseChallanDaybooks: [],
   purchaseOrderDaybooks: [],
   purchaseReturnDaybooks: [],
   issueDaybooks: [],
   receiveDaybooks: [],
});

export const useDaybook = () => useContext(DaybookContext);

export const DaybookProvider = ({ children }) => {
   const { user } = useUser();
   const { userGlobalSettingsData } = useGlobal();
   const daybooks = useSelector((state) => state?.daybook?.list);
   const dispatch = useDispatch();
   const authToken = localStorage.getItem('authTokenCustomer');

   const isDaybook = useMemo(() => userGlobalSettingsData?.[0]?.askCompany?.daybook || false, [userGlobalSettingsData]);

   // PAYMENT DAYBOOKS
   const paymentDaybooks = useMemo(() => {
      return Array.isArray(daybooks) && daybooks.length > 0
         ? daybooks?.filter((db) => {
              const companyIds = Array.isArray(db.companyIds) ? db.companyIds : [];
              const isCompany =
                 companyIds.includes(user?.loginhistory?.companyId) || !db.companyIds || companyIds.length === 0;
              return isCompany && (db.type === 13 || db.type === 14);
           })
         : [];
   }, [daybooks, user?.loginhistory?.companyId]);

   const salesDaybooks = useMemo(() => {
      return Array.isArray(daybooks) && daybooks.length > 0 ? daybooks?.filter((db) => db.type === 5) : [];
   }, [daybooks]);

   const salesChallanDaybooks = useMemo(() => {
      return Array.isArray(daybooks) && daybooks.length > 0 ? daybooks?.filter((db) => db.type === 4) : [];
   }, [daybooks]);

   const salesOrderDaybooks = useMemo(() => {
      return Array.isArray(daybooks) && daybooks.length > 0 ? daybooks?.filter((db) => db.type === 3) : [];
   }, [daybooks]);

   const salesReturnDaybooks = useMemo(() => {
      return Array.isArray(daybooks) && daybooks.length > 0 ? daybooks?.filter((db) => db.type === 6) : [];
   }, [daybooks]);

   const purchaseDaybooks = useMemo(() => {
      return Array.isArray(daybooks) && daybooks.length > 0 ? daybooks?.filter((db) => db.type === 11) : [];
   }, [daybooks]);

   const purchaseChallanDaybooks = useMemo(() => {
      return Array.isArray(daybooks) && daybooks.length > 0 ? daybooks?.filter((db) => db.type === 10) : [];
   }, [daybooks]);

   const purchaseOrderDaybooks = useMemo(() => {
      return Array.isArray(daybooks) && daybooks.length > 0 ? daybooks?.filter((db) => db.type === 9) : [];
   }, [daybooks]);

   const purchaseReturnDaybooks = useMemo(() => {
      return Array.isArray(daybooks) && daybooks.length > 0 ? daybooks?.filter((db) => db.type === 12) : [];
   }, [daybooks]);

   const issueDaybooks = useMemo(() => {
      return Array.isArray(daybooks) && daybooks.length > 0 ? daybooks?.filter((db) => db.type === 19) : [];
   }, [daybooks]);

   const receiveDaybooks = useMemo(() => {
      return Array.isArray(daybooks) && daybooks.length > 0 ? daybooks?.filter((db) => db.type === 20) : [];
   }, [daybooks]);

   useEffect(() => {
      if (user && user?.loginhistory?.companyId && authToken) {
         const payload = { companyId: user?.loginhistory?.companyId };
         if (isDaybook) {
            payload.companyIds = user?.loginhistory?.companyId;
         }
         dispatch(getDaybook(payload));
      }
   }, [user, dispatch, authToken, isDaybook]);

   return (
      <DaybookContext.Provider
         value={{
            daybooks,
            paymentDaybooks: Array.isArray(paymentDaybooks) && paymentDaybooks.length > 0 ? paymentDaybooks : [],
            salesChallanDaybooks:
               Array.isArray(salesChallanDaybooks) && salesChallanDaybooks.length > 0 ? salesChallanDaybooks : [],
            salesOrderDaybooks:
               Array.isArray(salesOrderDaybooks) && salesOrderDaybooks.length > 0 ? salesOrderDaybooks : [],
            salesDaybooks: Array.isArray(salesDaybooks) && salesDaybooks.length > 0 ? salesDaybooks : [],
            salesReturnDaybooks:
               Array.isArray(salesReturnDaybooks) && salesReturnDaybooks.length > 0 ? salesReturnDaybooks : [],
            purchaseChallanDaybooks:
               Array.isArray(purchaseChallanDaybooks) && purchaseChallanDaybooks.length > 0
                  ? purchaseChallanDaybooks
                  : [],
            purchaseOrderDaybooks:
               Array.isArray(purchaseOrderDaybooks) && purchaseOrderDaybooks.length > 0 ? purchaseOrderDaybooks : [],
            purchaseDaybooks: Array.isArray(purchaseDaybooks) && purchaseDaybooks.length > 0 ? purchaseDaybooks : [],
            purchaseReturnDaybooks:
               Array.isArray(purchaseReturnDaybooks) && purchaseReturnDaybooks.length > 0 ? purchaseReturnDaybooks : [],
            issueDaybooks: Array.isArray(issueDaybooks) && issueDaybooks.length > 0 ? issueDaybooks : [],
            receiveDaybooks: Array.isArray(receiveDaybooks) && receiveDaybooks.length > 0 ? receiveDaybooks : [],
            daybook: null,
         }}>
         {children}
      </DaybookContext.Provider>
   );
};
